import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ProjectPatch, ProjectSummary } from '../api/client.js';
import { ApiError, fetchProject, updateProject } from '../api/client.js';
import { useDictionary } from '../i18n/context.js';

// ---------------------------------------------------------------------------
// Brand context (UI structure spec 13.6): the six fields every authorized
// AI client reads through `get_project_context` -- product/service summary,
// official website URL, target audience, brand voice and tone, free-form AI
// instructions, and expressions/claims to avoid. This page states plainly
// that the context reaches AI clients (13.6: "The page explains that this
// context is available to authorized AI clients"), so an operator does not
// write something here believing it stays internal.
//
// Saving is explicit -- spec 19.1: "Project settings use explicit Save
// changes" -- never autosave. A single form state is loaded once from
// `GET /api/projects/:id` (Task 4's own read path, apps/worker/src/index.ts)
// and only written back on the Save action, via `PATCH /api/projects/:id`,
// which already creates the `project.updated` activity event server-side
// (that route's own audit.append call) -- this screen does not append a
// second one itself.
// ---------------------------------------------------------------------------

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'not_found' }
  | { status: 'ready' };

type SaveState = { status: 'idle' } | { status: 'saving' } | { status: 'saved' } | { status: 'error' };

type BrandContextForm = {
  website: string;
  productSummary: string;
  targetAudience: string;
  brandVoice: string;
  aiInstructions: string;
  expressionsToAvoid: string;
};

const toForm = (project: ProjectSummary): BrandContextForm => ({
  website: project.website ?? '',
  productSummary: project.productSummary ?? '',
  targetAudience: project.targetAudience ?? '',
  brandVoice: project.brandVoice ?? '',
  aiInstructions: project.aiInstructions ?? '',
  expressionsToAvoid: project.expressionsToAvoid ?? '',
});

const emptyToNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

const BRAND_CONTEXT_FIELDS = [
  'website', 'productSummary', 'targetAudience', 'brandVoice', 'aiInstructions', 'expressionsToAvoid',
] as const satisfies readonly (keyof BrandContextForm)[];

// Review fix (Important 2): this used to emit all six keys on every save,
// which defeats `PATCH /api/projects/:id`'s own omitted-key-means-leave-
// alone contract (apps/worker/src/index.ts's own comment on `ProjectUpdate`,
// pinned by Task 3's tests). Concretely: an MCP client sets `brandVoice`
// while the operator's form is open; the operator edits only
// `targetAudience` and saves; sending all six keys silently reverts
// `brandVoice` to the value the form loaded, with no warning to anyone.
//
// The fix is to send only what the operator actually touched: `loaded` is
// the form as it was read from the server (or as of the last successful
// save -- see `BrandContextRoute`'s own `original` state), `current` is the
// live draft, and a key is included only when the two differ *after*
// normalization (`emptyToNull`) -- so re-saving unchanged whitespace, or a
// value that round-trips to the same `null`, does not spuriously mark a
// field as touched. This restores the PATCH contract's intent: an
// untouched field is never transmitted, so a concurrent MCP write to it
// survives the operator's save.
//
// What this does NOT solve: the operator and an MCP client editing the
// *same* field concurrently. That still resolves as "whoever saves last
// wins" with no warning, because detecting it requires a revision
// identifier and the stale-data flow spec section 11.7 describes -- and
// `projects` has no revision column app-wide (not something this screen can
// add on its own). Spec 13.6 names this exact gap: "If MCP updates the
// context while the form has unsaved changes, the stale-data behavior in
// section 11.7 applies" -- section 11.7's machinery is simply not built
// yet, anywhere in this app, so it is not built here either. Recorded as a
// known gap, not assumed closed.
//
// Exported and tested directly, the way `connectionView` (project.tsx) and
// `automationStatusLabel` (projects.tsx) are.
export const buildBrandContextPatch = (loaded: BrandContextForm, current: BrandContextForm): ProjectPatch => {
  const patch: ProjectPatch = {};
  for (const field of BRAND_CONTEXT_FIELDS) {
    const before = emptyToNull(loaded[field]);
    const after = emptyToNull(current[field]);
    if (before !== after) {
      patch[field] = after;
    }
  }
  return patch;
};

export const BrandContextRoute = (): React.JSX.Element => {
  const dict = useDictionary();
  const { projectId } = useParams<{ projectId: string }>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [form, setForm] = useState<BrandContextForm | null>(null);
  // The form as last read from the server -- the baseline `buildBrandContextPatch`
  // diffs the live draft against, so only fields the operator actually
  // changed are ever sent (see that function's own comment). Updated
  // alongside `form` on both load and successful save, never on every
  // keystroke -- keystrokes only move `form`.
  const [original, setOriginal] = useState<BrandContextForm | null>(null);
  const [saveState, setSaveState] = useState<SaveState>({ status: 'idle' });

  const load = (): void => {
    if (!projectId) return;
    setState({ status: 'loading' });
    fetchProject(projectId)
      .then((res) => {
        const next = toForm(res.project);
        setForm(next);
        setOriginal(next);
        setState({ status: 'ready' });
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setState({ status: 'not_found' });
        } else {
          setState({ status: 'error' });
        }
      });
  };

  useEffect(load, [projectId]);

  const updateField = (field: keyof BrandContextForm, value: string): void => {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
    setSaveState({ status: 'idle' });
  };

  const save = (): void => {
    if (!projectId || !form || !original) return;
    setSaveState({ status: 'saving' });
    updateProject(projectId, buildBrandContextPatch(original, form))
      .then((res) => {
        const next = toForm(res.project);
        setForm(next);
        setOriginal(next);
        setSaveState({ status: 'saved' });
      })
      .catch(() => setSaveState({ status: 'error' }));
  };

  if (!projectId) return <p className="t-body">{dict.ui.brandContext.notFound}</p>;

  return (
    <div className="stack" style={{ gap: 'var(--sp-5)' }}>
      <Link className="btn btn--ghost" to={`/projects/${projectId}`}>{dict.ui.brandContext.backToProject}</Link>
      <h1 className="t-title">{dict.ui.brandContext.title}</h1>
      <p className="t-body" style={{ maxWidth: '68ch' }}>{dict.ui.brandContext.intro}</p>

      {state.status === 'loading' && <p className="t-meta">{dict.ui.brandContext.loading}</p>}
      {state.status === 'not_found' && <p className="t-body">{dict.ui.brandContext.notFound}</p>}

      {state.status === 'error' && (
        <div className="alert alert--danger">
          <div className="stack-1">
            <div className="alert__title">{dict.ui.brandContext.loadFailed}</div>
            <button type="button" className="btn" onClick={load}>{dict.ui.activity.retry}</button>
          </div>
        </div>
      )}

      {state.status === 'ready' && form && (
        <div className="panel">
          <div className="panel__body stack">
            <div className="field">
              <label className="field__label" htmlFor="brand-product-summary">{dict.ui.brandContext.productSummaryLabel}</label>
              <textarea
                id="brand-product-summary"
                className="textarea"
                value={form.productSummary}
                onChange={(e) => updateField('productSummary', e.target.value)}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="brand-website">{dict.ui.brandContext.websiteLabel}</label>
              <input
                id="brand-website"
                className="input"
                type="text"
                value={form.website}
                onChange={(e) => updateField('website', e.target.value)}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="brand-target-audience">{dict.ui.brandContext.targetAudienceLabel}</label>
              <textarea
                id="brand-target-audience"
                className="textarea"
                value={form.targetAudience}
                onChange={(e) => updateField('targetAudience', e.target.value)}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="brand-voice">{dict.ui.brandContext.brandVoiceLabel}</label>
              <textarea
                id="brand-voice"
                className="textarea"
                value={form.brandVoice}
                onChange={(e) => updateField('brandVoice', e.target.value)}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="brand-ai-instructions">{dict.ui.brandContext.aiInstructionsLabel}</label>
              <textarea
                id="brand-ai-instructions"
                className="textarea"
                value={form.aiInstructions}
                onChange={(e) => updateField('aiInstructions', e.target.value)}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="brand-expressions-to-avoid">{dict.ui.brandContext.expressionsToAvoidLabel}</label>
              <textarea
                id="brand-expressions-to-avoid"
                className="textarea"
                value={form.expressionsToAvoid}
                onChange={(e) => updateField('expressionsToAvoid', e.target.value)}
              />
            </div>

            {saveState.status === 'error' && (
              <div className="field__error">{dict.ui.brandContext.saveFailed}</div>
            )}

            <div className="row" style={{ gap: 'var(--sp-3)' }}>
              <button
                type="button"
                className="btn btn--primary"
                disabled={saveState.status === 'saving'}
                onClick={save}
              >
                {saveState.status === 'saving' ? dict.ui.brandContext.saving : dict.ui.brandContext.save}
              </button>
              {saveState.status === 'saved' && <span className="t-meta">{dict.ui.brandContext.saved}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
